import { deserializeProject, serializeProject } from '@/project/ProjectSerializer';
import { summarizeProject, type LandscapeProject } from '@/project/Project';
import type { ProjectStorage, StoredProjectSummary } from './ProjectStorage';

/**
 * Local persistence for saved sites (milestone 7.2).
 *
 * Follows the open/upgrade shape established by `capture/PhotoStore`, including its memory fallback:
 * when IndexedDB is unavailable (private browsing, blocked storage) saving still works for the
 * session rather than failing outright — the UI just tells the user the save will not outlive the
 * tab. Documents are stored serialized, so a record written by an older build is validated on read
 * by the same code path that reads an imported file.
 *
 * This is one of two implementations of {@link ProjectStorage}; the other keeps sites in a signed-in
 * account. The interface is what lets the editor be written once — see that file for why
 * `summaries` and `rename` are separate operations rather than things a caller derives from
 * `list()`. Here they are derived from `list()`, because reading the whole library off local disk
 * costs nothing.
 */

/**
 * Where this browser remembers the site it last had open.
 *
 * Deliberately `localStorage` and not a row in the database: the pointer is about this browser, not
 * about the library, and keeping it out of the object store means it cannot be lost to a schema
 * upgrade, cannot be copied into an account by the import flow, and needs no version bump to add.
 * The hosted store keeps the same fact on the server, where it can follow the user to another
 * machine -- see `ProjectStorage.resumeSite`.
 */
const LAST_OPENED_KEY = 'landscape-glade.last-opened';

const DATABASE_NAME = 'landscape-glade-projects';
const DATABASE_VERSION = 1;
const PROJECT_STORE = 'projects';

export class ProjectStore implements ProjectStorage {
  private database: IDBDatabase | null = null;
  private opening: Promise<IDBDatabase | null> | null = null;
  private readonly memory = new Map<string, LandscapeProject>();
  private useMemory = false;

  /** Whether saves will survive a reload. False when running on the memory fallback. */
  get persistent(): boolean {
    return !this.useMemory;
  }

  /** Insert or replace a project, stamping `updatedAt`. Returns the record as stored. */
  async save(project: LandscapeProject): Promise<LandscapeProject> {
    const record = serializeProject({ ...project, updatedAt: new Date().toISOString() });
    const database = await this.open();
    if (!database) {
      this.memory.set(record.id, record);
      return record;
    }
    await request(this.transaction(database, 'readwrite').put(record));
    return record;
  }

  /** All saved projects, most recently updated first. Unreadable records are skipped. */
  async list(): Promise<LandscapeProject[]> {
    const database = await this.open();
    const raw = database
      ? await request<unknown[]>(
          this.transaction(database, 'readonly').getAll() as IDBRequest<unknown[]>,
        )
      : [...this.memory.values()];

    const projects: LandscapeProject[] = [];
    for (const entry of raw) {
      try {
        projects.push(deserializeProject(entry));
      } catch (error) {
        // One corrupt record must not take the whole library down with it.
        console.warn('Skipping unreadable saved site.', error);
      }
    }
    return projects.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  }

  /**
   * Metadata for the library screen.
   *
   * Every document is already in hand once `list()` has run, so the counts are computed here rather
   * than stored. That keeps them incapable of going stale, which the cloud store — where they must
   * be denormalized — has to work to guarantee.
   */
  async summaries(): Promise<StoredProjectSummary[]> {
    return (await this.list()).map((project) => ({
      id: project.id,
      name: project.name,
      createdAt: project.createdAt,
      updatedAt: project.updatedAt,
      stats: summarizeProject(project),
    }));
  }

  /** Rename a stored site. Read-modify-write, since IndexedDB stores whole records. */
  async rename(id: string, name: string): Promise<void> {
    const project = await this.get(id);
    if (!project) return;
    await this.save({ ...project, name });
  }

  async get(id: string): Promise<LandscapeProject | undefined> {
    const database = await this.open();
    const raw = database
      ? await request<unknown>(this.transaction(database, 'readonly').get(id) as IDBRequest<unknown>)
      : this.memory.get(id);
    if (raw === undefined || raw === null) return undefined;
    return deserializeProject(raw);
  }

  /** Remember this site as the one to resume onto. Storage failures are not worth an exception. */
  markOpened(id: string): Promise<void> {
    try {
      globalThis.localStorage?.setItem(LAST_OPENED_KEY, id);
    } catch {
      // Blocked storage: the session simply does not resume next time.
    }
    return Promise.resolve();
  }

  /**
   * The site to reopen, if it is still here.
   *
   * Checked against the library rather than returned on trust, because the pointer outlives what it
   * points at: a site deleted in another tab, or a library cleared entirely, would otherwise send
   * the next session looking for a document that no longer exists.
   */
  async resumeSite(): Promise<string | null> {
    let id: string | null = null;
    try {
      id = globalThis.localStorage?.getItem(LAST_OPENED_KEY) ?? null;
    } catch {
      return null;
    }
    if (!id) return null;
    return (await this.get(id)) ? id : null;
  }

  async remove(id: string): Promise<void> {
    const database = await this.open();
    if (!database) {
      this.memory.delete(id);
      return;
    }
    await request(this.transaction(database, 'readwrite').delete(id));
  }

  close(): void {
    this.database?.close();
    this.database = null;
    this.opening = null;
  }

  private transaction(database: IDBDatabase, mode: IDBTransactionMode): IDBObjectStore {
    return database.transaction(PROJECT_STORE, mode).objectStore(PROJECT_STORE);
  }

  /** Open (and upgrade) the database once, memoised. Resolves null on the memory fallback. */
  private open(): Promise<IDBDatabase | null> {
    if (this.database) return Promise.resolve(this.database);
    if (this.opening) return this.opening;

    if (typeof indexedDB === 'undefined') {
      this.useMemory = true;
      return Promise.resolve(null);
    }

    this.opening = new Promise<IDBDatabase | null>((resolve) => {
      let open: IDBOpenDBRequest;
      try {
        open = indexedDB.open(DATABASE_NAME, DATABASE_VERSION);
      } catch {
        this.useMemory = true;
        resolve(null);
        return;
      }

      open.onupgradeneeded = () => {
        const database = open.result;
        if (!database.objectStoreNames.contains(PROJECT_STORE)) {
          database.createObjectStore(PROJECT_STORE, { keyPath: 'id' });
        }
      };
      open.onsuccess = () => {
        this.database = open.result;
        this.useMemory = false;
        resolve(this.database);
      };
      open.onerror = () => {
        console.warn('Project storage unavailable; saves will not persist.', open.error);
        this.useMemory = true;
        resolve(null);
      };
      open.onblocked = () => {
        this.useMemory = true;
        resolve(null);
      };
    });

    return this.opening;
  }
}

function request<T = unknown>(source: IDBRequest<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    source.onsuccess = () => resolve(source.result);
    source.onerror = () => reject(source.error ?? new Error('IndexedDB request failed.'));
  });
}
